/**
 * The finance system, read rather than exported.
 *
 * The executive desks have taken their money from a monthly file somebody
 * saved out of the finance system and uploaded here. That is honest and it
 * does not survive a month-end: the file is a fortnight old by the time
 * anybody reads the desk, and nobody can say whether the period it covers is
 * still moving.
 *
 * This reads S/4HANA's Universal Journal instead — the line items the
 * standard OData service publishes — and the field names below are SAP's own,
 * not friendlier ones. A connector that renames everything at the boundary is
 * a connector nobody can debug against the source system.
 *
 * ── the sign, which is where these go wrong ─────────────────────────────
 *
 * SAP stores revenue as a CREDIT. Read the amount without reading the
 * debit/credit indicator and revenue arrives as a negative cost, the operating
 * result inverts, and every figure downstream is confidently wrong. So the
 * sign here comes from `DebitCreditCode` — 'S' debit, 'H' credit — and a line
 * whose indicator is missing or unrecognised is REJECTED rather than assumed.
 * There is no defensible default.
 *
 * ── open periods are not results ────────────────────────────────────────
 *
 * A fiscal period in SAP is open or closed. A figure from an open period can
 * still change — late postings, accruals, reversals — and presenting it as a
 * result is the finance equivalent of showing an estimate as an actual. The
 * period's state comes back with the figures and travels with them.
 *
 * ── a reversal is not a transaction ─────────────────────────────────────
 *
 * A reversed document and its reversal both exist in the journal. Counting
 * either is double counting; counting both is zero. Both are dropped, and
 * the count of what was dropped is reported.
 *
 * Nothing here calls anything. It is the contract, the mapping and the
 * checks — the shape a read takes — so that the day a connection exists the
 * screens above it do not change.
 *
 * That day has come on the platform: backend/api/slt/finance/ reads SAP
 * itself (credentials cannot live in a browser), and its read.py is this
 * read, rule for rule, in Python. Both are held to one recorded fixture
 * (sap.agreement.test.ts, and the backend's test_sap_connector.py), so a fix
 * to one that is not made to the other fails a test on both sides.
 */
import { t, tn } from "../i18n";
import type { CapexLine, Finance, FinanceLine, MonthLine } from "./finance";

/* ── what S/4HANA returns ─────────────────────────────────────────────── */

/**
 * One line of the Universal Journal, in SAP's own field names.
 *
 * A subset: the fields a management pack is built from. Anything else the
 * service returns is carried through untouched rather than dropped, because
 * a connector that discards what it does not understand makes the next
 * question unanswerable.
 */
export interface JournalLine {
  CompanyCode: string;
  FiscalYear: string;
  FiscalPeriod: string;
  AccountingDocument?: string;
  GLAccount: string;
  GLAccountName?: string;
  CostCenter?: string;
  ProfitCenter?: string;
  /** the amount in the company code's own currency, always positive in SAP */
  AmountInCompanyCodeCurrency: number | string;
  CompanyCodeCurrency: string;
  /** 'S' debit, 'H' credit. The whole sign of the result rests on this. */
  DebitCreditCode?: string;
  PostingDate?: string;
  DocumentDate?: string;
  /** SAP marks a document that has been reversed, and the reversal itself */
  IsReversed?: boolean;
  IsReversal?: boolean;
  [field: string]: unknown;
}

export type PeriodState = "open" | "closed" | "unknown";

export interface SapPeriod {
  companyCode: string;
  fiscalYear: string;
  fiscalPeriod: string;
  state: PeriodState;
  /** when the period was closed, where it is */
  closedAt?: string;
}

export interface SapResponse {
  /** the system this came from, as the connection names it */
  system: string;
  /** the service the lines were read from, so a reader can go and look */
  service: string;
  readAt: string;
  period: SapPeriod;
  lines: JournalLine[];
  /**
   * The plan, if the connection reads it.
   *
   * The Universal Journal holds what HAPPENED. The plan lives in a different
   * table — ACDOCP — and a connection that reads only the journal has
   * actuals and no budget. That matters more than it sounds: every desk
   * downstream computes variance against budget, so a missing plan read as
   * zero turns into "every line is over plan by its entire cost", which is a
   * finding the airport does not have. Optional here, and its absence is
   * carried through to the screens rather than filled in.
   */
  plan?: PlanLine[];
}

/**
 * One plan line, from ACDOCP.
 *
 * The plan carries a signed amount rather than a debit/credit indicator —
 * SAP's plan table differs from the journal here, and pretending they are the
 * same shape is how a connector inverts half its budgets.
 */
export interface PlanLine {
  CompanyCode: string;
  FiscalYear: string;
  FiscalPeriod: string;
  GLAccount: string;
  AmountInCompanyCodeCurrency: number | string;
  CompanyCodeCurrency: string;
  /** which plan: an airport usually carries several versions */
  PlanningCategory?: string;
  [field: string]: unknown;
}

/* ── reading one line ─────────────────────────────────────────────────── */

export type Rejection = "no_amount" | "no_sign" | "reversed" | "wrong_currency" | "no_account";

export const REJECTION_LABEL: Record<Rejection, string> = {
  no_amount: "the amount could not be read",
  no_sign: "no debit or credit indicator, so the sign is unknown",
  reversed: "a reversed document, or the reversal of one",
  wrong_currency: "not in the company code's own currency",
  no_account: "no G/L account",
};

export interface ReadLine {
  line: JournalLine;
  /** positive is money in, negative is money out — the platform's convention */
  signed: number;
  account: string;
  name: string;
}

/**
 * One journal line, with its sign resolved.
 *
 * The convention here is the platform's, not SAP's: positive is money coming
 * in. SAP's own amounts are unsigned and the direction lives in
 * `DebitCreditCode`, so this is the one place the two conventions meet and it
 * is written out in full rather than inferred.
 */
export function readLine(line: JournalLine, currency: string): ReadLine | { rejected: Rejection } {
  if (line.IsReversed || line.IsReversal) return { rejected: "reversed" };
  if (!line.GLAccount) return { rejected: "no_account" };
  if (line.CompanyCodeCurrency && currency && line.CompanyCodeCurrency !== currency) {
    return { rejected: "wrong_currency" };
  }
  const raw = typeof line.AmountInCompanyCodeCurrency === "string"
    ? Number(line.AmountInCompanyCodeCurrency.replace(/,/g, ""))
    : line.AmountInCompanyCodeCurrency;
  if (!Number.isFinite(raw)) return { rejected: "no_amount" };

  const indicator = (line.DebitCreditCode ?? "").trim().toUpperCase();
  if (indicator !== "S" && indicator !== "H") return { rejected: "no_sign" };
  /* credit is money in; debit is money out */
  const signed = indicator === "H" ? Math.abs(raw) : -Math.abs(raw);

  return { line, signed, account: line.GLAccount, name: line.GLAccountName ?? line.GLAccount };
}

/* ── where an account goes ────────────────────────────────────────────── */

export type SapTarget =
  | { kind: "revenue"; key: string; label: string }
  | { kind: "opex"; key: string; label: string }
  | { kind: "capex" }
  | { kind: "ignore"; why: string };

export interface AccountRule {
  /** the G/L account range this covers, inclusive */
  from: string;
  to: string;
  target: SapTarget;
  /** said in words, so somebody can disagree with the rule rather than the number */
  why: string;
}

/**
 * The chart of accounts, in ranges.
 *
 * SAP charts are laid out in numeric blocks and an operator's mapping is
 * nearly always "these accounts are that". Ranges rather than a list of
 * accounts, because a new account opened inside a block should land in the
 * right place without anybody editing this file — and one opened OUTSIDE
 * every block should be reported, not guessed at.
 *
 * These ranges are the ones an airport's chart usually uses. PROPOSED: every
 * operator's chart differs, and the day this is connected the ranges come
 * from the operator rather than from here.
 */
export const ACCOUNT_RULES: AccountRule[] = [
  { from: "400000", to: "409999", target: { kind: "revenue", key: "aeronautical", label: "Aeronautical revenue" }, why: "aeronautical revenue block" },
  { from: "410000", to: "419999", target: { kind: "revenue", key: "commercial", label: "Commercial revenue" }, why: "commercial and concession revenue block" },
  { from: "420000", to: "429999", target: { kind: "revenue", key: "other", label: "Other revenue" }, why: "other operating income" },
  { from: "500000", to: "509999", target: { kind: "opex", key: "staff", label: "Staff costs" }, why: "personnel cost block" },
  { from: "510000", to: "519999", target: { kind: "opex", key: "maintenance", label: "Maintenance and repair" }, why: "maintenance and repair block" },
  { from: "520000", to: "529999", target: { kind: "opex", key: "utilities", label: "Utilities" }, why: "energy and utilities block" },
  { from: "530000", to: "539999", target: { kind: "opex", key: "services", label: "Contracted services" }, why: "purchased services block" },
  { from: "540000", to: "549999", target: { kind: "opex", key: "admin", label: "Administration" }, why: "general administration block" },
  { from: "600000", to: "609999", target: { kind: "opex", key: "other", label: "Other operating cost" }, why: "other operating expense" },
  { from: "100000", to: "199999", target: { kind: "capex" }, why: "asset under construction and fixed asset block" },
  { from: "800000", to: "899999", target: { kind: "ignore", why: "balance sheet and statistical accounts, which are not a management figure" }, why: "balance sheet block" },
];

/**
 * The account as the rules compare it.
 *
 * S/4HANA returns G/L accounts zero-padded to ten characters ("0000400100")
 * and every range above is written the way people say them ("400100"). A
 * string comparison of the two puts every padded account outside every rule,
 * so an all-digit account loses its leading zeros before it is compared. The
 * account itself is still reported as SAP wrote it. The backend's read
 * (backend/api/slt/finance/read.py) does exactly the same.
 */
export function accountKey(account: string): string {
  return /^\d+$/.test(account) ? account.replace(/^0+(?=\d)/, "") : account;
}

/**
 * The rule that places an account: the first whose range holds it.
 *
 * `rules` is a site's own ranges followed by these, when its connection
 * carries overrides — the backend hands back the list it used, so the screen
 * shows the rules that placed the money rather than the proposed ones.
 */
export function ruleFor(account: string, rules: AccountRule[] = ACCOUNT_RULES): AccountRule | null {
  const key = accountKey(account);
  return rules.find((r) => key >= r.from && key <= r.to) ?? null;
}

/* ── the read ─────────────────────────────────────────────────────────── */

export interface Unbudgeted {
  key: string;
  label: string;
  /** "none" — no plan line at all; "partial" — a plan that covers less than the actual */
  why: "none" | "partial";
  /** accounts in this line that posted an actual */
  accounts: number;
  /** how many of those the plan covers */
  planned: number;
}

export interface Unmapped {
  account: string;
  name: string;
  amount: number;
  lines: number;
}

export interface SapRead {
  system: string;
  service: string;
  readAt: string;
  period: SapPeriod;
  currency: string;
  lines: number;
  mapped: number;
  rejected: { reason: Rejection; lines: number; amount: number }[];
  /** accounts no rule covers, with their money — never swept into "other" */
  unmapped: Unmapped[];
  revenue: FinanceLine[];
  opex: FinanceLine[];
  capex: number;
  /**
   * Whether a plan was read at all, and how far it reached.
   *
   * There are three states, not two, and conflating the middle one with the
   * first is how a connector fabricates variance.
   *
   *   planned       every account with an actual also has a plan line
   *   partly planned  some do — the plan is real but covers less than the actual
   *   not planned   none do
   *
   * The middle case is the dangerous one. An operator plans at the level it
   * manages: one plan line against a block of four accounts that all post
   * actuals. Presenting that plan line as the whole line's budget compares a
   * full actual against a quarter of a plan and reports the airport as
   * catastrophically over budget, when nothing is wrong but the arithmetic.
   *
   * So `budget` above is meaningful only where `budgeted` names the key.
   * Everywhere else the budget equals the actual, which claims no variance
   * rather than inventing one, and `unbudgeted` says which lines and why.
   */
  planned: boolean;
  budgeted: string[];
  unbudgeted: Unbudgeted[];
  /** the totals, so a reader can check the arithmetic against SAP's own */
  totals: { revenue: number; opex: number; capex: number; unmapped: number };
}

/**
 * The plan, summed by the same rules the actuals went through.
 *
 * Deliberately the same `ruleFor` and the same targets: a plan mapped by a
 * second set of rules produces variances that are an artefact of the mapping
 * rather than of the airport's spending, and nobody downstream can tell the
 * difference.
 */
function readPlan(
  plan: PlanLine[] | undefined,
  currency: string,
  rules: AccountRule[],
): Map<string, { amount: number; accounts: Set<string> }> {
  const out = new Map<string, { amount: number; accounts: Set<string> }>();
  for (const line of plan ?? []) {
    if (line.CompanyCodeCurrency && currency && line.CompanyCodeCurrency !== currency) continue;
    const raw = typeof line.AmountInCompanyCodeCurrency === "string"
      ? Number(line.AmountInCompanyCodeCurrency.replace(/,/g, ""))
      : line.AmountInCompanyCodeCurrency;
    if (!Number.isFinite(raw)) continue;
    const rule = ruleFor(line.GLAccount, rules);
    if (!rule || rule.target.kind === "ignore" || rule.target.kind === "capex") continue;
    const key = rule.target.key;
    const entry = out.get(key) ?? { amount: 0, accounts: new Set<string>() };
    entry.amount += Math.abs(raw);
    entry.accounts.add(line.GLAccount);
    out.set(key, entry);
  }
  return out;
}

/**
 * Read a response into the figures the desks use.
 *
 * Every line goes into exactly one of four places: a target, the unmapped
 * list, the rejected list, or the ignore rules. Nothing is dropped silently,
 * and the four counts add up to the number of lines that came back — which a
 * test pins, because a connector that loses money between the wire and the
 * screen is worse than no connector.
 */
export function readSap(response: SapResponse, rules: AccountRule[] = ACCOUNT_RULES): SapRead {
  const currency = response.lines[0]?.CompanyCodeCurrency ?? "SAR";
  const rejected = new Map<Rejection, { lines: number; amount: number }>();
  const unmapped = new Map<string, Unmapped>();
  const revenue = new Map<string, FinanceLine>();
  const opex = new Map<string, FinanceLine>();
  /* which G/L accounts actually posted against each line, for the plan check */
  const accountsSeen = new Map<string, Set<string>>();
  let capex = 0;
  let mapped = 0;

  for (const raw of response.lines) {
    const read = readLine(raw, currency);
    if ("rejected" in read) {
      const entry = rejected.get(read.rejected) ?? { lines: 0, amount: 0 };
      entry.lines += 1;
      const amount = Number(raw.AmountInCompanyCodeCurrency);
      entry.amount += Number.isFinite(amount) ? Math.abs(amount) : 0;
      rejected.set(read.rejected, entry);
      continue;
    }
    const rule = ruleFor(read.account, rules);
    if (!rule) {
      const entry = unmapped.get(read.account) ?? { account: read.account, name: read.name, amount: 0, lines: 0 };
      entry.amount += read.signed;
      entry.lines += 1;
      unmapped.set(read.account, entry);
      continue;
    }
    mapped += 1;
    const target = rule.target;
    if (target.kind === "ignore") continue;
    if (target.kind === "capex") {
      capex += Math.abs(read.signed);
      continue;
    }
    const into = target.kind === "revenue" ? revenue : opex;
    const seen = accountsSeen.get(target.key) ?? new Set<string>();
    seen.add(read.account);
    accountsSeen.set(target.key, seen);
    const line = into.get(target.key) ?? { key: target.key, label: target.label, actual: 0, budget: 0 };
    /* opex is money out, and the desks want it as a positive cost */
    line.actual += target.kind === "revenue" ? read.signed : Math.abs(read.signed);
    into.set(target.key, line);
  }

  const planned = readPlan(response.plan, currency, rules);
  const budgeted: string[] = [];
  const unbudgeted: Unbudgeted[] = [];
  for (const line of [...revenue.values(), ...opex.values()]) {
    const plan = planned.get(line.key);
    const posted = accountsSeen.get(line.key) ?? new Set<string>();
    const covered = plan ? [...posted].filter((a) => plan.accounts.has(a)).length : 0;
    /*
     * The plan is taken as this line's budget only when it covers every
     * account that posted an actual. A plan covering three accounts out of
     * four is a real plan for a smaller thing, and setting it against the
     * whole line's actual reports an overspend that is an artefact of the
     * comparison. In that case the budget repeats the actual — no variance
     * claimed — and the line is named below with how far the plan reached.
     */
    if (!plan || covered < posted.size) {
      line.budget = line.actual;
      unbudgeted.push({
        key: line.key,
        label: line.label,
        why: plan ? "partial" : "none",
        accounts: posted.size,
        planned: covered,
      });
    } else {
      line.budget = plan.amount;
      budgeted.push(line.key);
    }
  }

  const revenueLines = [...revenue.values()].sort((a, b) => b.actual - a.actual);
  const opexLines = [...opex.values()].sort((a, b) => b.actual - a.actual);
  return {
    system: response.system,
    service: response.service,
    readAt: response.readAt,
    period: response.period,
    currency,
    lines: response.lines.length,
    mapped,
    rejected: [...rejected.entries()].map(([reason, v]) => ({ reason, ...v })).sort((a, b) => b.lines - a.lines),
    unmapped: [...unmapped.values()].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount)),
    revenue: revenueLines,
    opex: opexLines,
    capex,
    planned: (response.plan?.length ?? 0) > 0,
    budgeted,
    unbudgeted,
    totals: {
      revenue: revenueLines.reduce((n, l) => n + l.actual, 0),
      opex: opexLines.reduce((n, l) => n + l.actual, 0),
      capex,
      unmapped: [...unmapped.values()].reduce((n, u) => n + u.amount, 0),
    },
  };
}

/** One line about what came back, honest about what it could not place. */
export function readSentence(read: SapRead): string {
  const parts = [
    t("{n} read from {system}.", { n: tn(read.lines, "{n} journal line", "{n} journal lines"), system: read.system }),
  ];
  if (read.unmapped.length > 0) {
    parts.push(
      tn(
        read.unmapped.length,
        "{n} account is covered by no rule and its money is not in any total below.",
        "{n} accounts are covered by no rule and their money is not in any total below.",
      ),
    );
  }
  const dropped = read.rejected.reduce((n, r) => n + r.lines, 0);
  if (dropped > 0) {
    parts.push(tn(dropped, "{n} line was not counted.", "{n} lines were not counted."));
  }
  if (!read.planned) {
    parts.push(t("No plan was read, so nothing here is compared against a budget."));
  } else if (read.unbudgeted.length > 0) {
    const partial = read.unbudgeted.filter((u) => u.why === "partial").length;
    parts.push(
      tn(
        read.unbudgeted.length,
        "{n} line shows no variance because the plan does not cover it, not because it is on plan.",
        "{n} lines show no variance because the plan does not cover them, not because they are on plan.",
      ),
    );
    if (partial > 0) {
      parts.push(
        tn(
          partial,
          "On {n} of those the plan is real but reaches fewer accounts than the actual does.",
          "On {n} of those the plan is real but reaches fewer accounts than the actuals do.",
        ),
      );
    }
  }
  if (read.period.state === "open") {
    parts.push(t("The period is still open, so these figures can change."));
  }
  return parts.join(" ");
}

/* ── into the shape the desks share ───────────────────────────────────── */

/**
 * The desks' `Finance`, from a read.
 *
 * `basis` becomes "ledger" — the desks already test `=== "demo"` to decide
 * whether a figure is real, so a read counts as real without touching nine
 * screens. What changes is the provenance block: it names the system, the
 * service, the period and whether that period is closed, rather than a
 * filename and an uploader.
 */
export function toFinance(
  read: SapRead,
  base: { period: string; daysElapsed: number; passengers?: number; monthly?: MonthLine[]; capexProjects?: CapexLine[] },
): Finance {
  return {
    currency: read.currency,
    period: base.period,
    daysElapsed: base.daysElapsed,
    passengers: base.passengers,
    revenue: read.revenue,
    opex: {
      key: "opex",
      label: t("Operating cost"),
      actual: read.totals.opex,
      budget: read.opex.reduce((n, l) => n + l.budget, 0),
    },
    opexByArea: read.opex,
    capex: base.capexProjects ?? [],
    monthly: base.monthly ?? [],
    basis: "ledger",
    ledger: {
      file: t("{system} · {service}", { system: read.system, service: read.service }),
      /* the desks render this as "read from {file} by {who}", and the honest
         answer to who is nobody — the connection read it */
      importedBy: t("the connector, with nobody in between"),
      importedAt: read.readAt,
      rows: read.lines,
      mapped: read.mapped,
      unmapped: read.unmapped.length,
      rejected: read.rejected.reduce((n, r) => n + r.lines, 0),
      period: t("{year} period {period} — {state}", {
        year: read.period.fiscalYear,
        period: read.period.fiscalPeriod,
        state: t(read.period.state),
      }),
    },
  };
}

/* ── checking it against what the platform already knows ──────────────── */

export interface Reconciliation {
  key: string;
  label: string;
  /** what the finance system says */
  ledger: number;
  /** what this platform's own records add up to */
  platform: number;
  difference: number;
  /** the share of the ledger figure the difference represents */
  pct: number | null;
  says: string;
}

/**
 * The ledger against the platform's own records.
 *
 * Neither side is corrected. The finance system is the authority on what was
 * spent and this platform is the authority on what the work was — and where
 * they differ, the difference is the finding. A connector that quietly
 * adjusted one to match the other would destroy the only thing the comparison
 * is for.
 *
 * A difference inside the tolerance is reported as agreement rather than as
 * zero, because two systems that agree to the riyal are usually one system
 * copying the other.
 */
export const RECONCILE_TOLERANCE_PCT = 2;

export function reconcile(
  read: SapRead,
  platform: { maintenanceSpend?: number; capexSpend?: number },
): Reconciliation[] {
  const out: Reconciliation[] = [];
  const compare = (key: string, label: string, ledger: number, own: number | undefined) => {
    if (own === undefined) return;
    const difference = own - ledger;
    const pct = ledger === 0 ? null : Math.round((Math.abs(difference) / Math.abs(ledger)) * 1000) / 10;
    out.push({
      key,
      label,
      ledger,
      platform: own,
      difference,
      pct,
      says:
        pct !== null && pct <= RECONCILE_TOLERANCE_PCT
          ? t("{label}: the two agree within {pct}%.", { label, pct })
          : t(
              "{label}: the finance system has {ledger} and this platform's own records add to {platform} — a difference of {diff}.",
              {
                label,
                ledger: Math.round(ledger).toLocaleString(),
                platform: Math.round(own).toLocaleString(),
                diff: Math.round(Math.abs(difference)).toLocaleString(),
              },
            ),
    });
  };
  compare(
    "maintenance",
    t("Maintenance cost"),
    read.opex.find((l) => l.key === "maintenance")?.actual ?? 0,
    platform.maintenanceSpend,
  );
  compare("capex", t("Capital spend"), read.capex, platform.capexSpend);
  return out;
}

/* ── how old is it ────────────────────────────────────────────────────── */

/**
 * How stale a read is, and what that means.
 *
 * A connector's worst failure is silent: it stops reading, the screens keep
 * showing the last figures, and nobody notices until the numbers are a month
 * old. Freshness is reported beside every figure that came from it.
 */
export function freshness(read: SapRead, now: Date): { hours: number; stale: boolean; says: string } {
  const hours = Math.max(0, Math.round((now.getTime() - Date.parse(read.readAt)) / 3_600_000));
  const stale = hours > 24;
  return {
    hours,
    stale,
    says: stale
      ? t("Last read {n} ago. Nothing here has changed since, whatever the finance system has done.", {
          n: hours > 48 ? tn(Math.round(hours / 24), "{n} day", "{n} days") : tn(hours, "{n} hour", "{n} hours"),
        })
      : t("Read {n} ago.", { n: hours < 1 ? t("less than an hour") : tn(hours, "{n} hour", "{n} hours") }),
  };
}
